<template>
  <DragPanel
    title="水面编辑"
    :closePanel="closePanel"
    :tooltipVisible="false"
    ref="dom">
    <div class="edit-water-box">
      <div class="steps-box">
        <div class="step-item-box">
          <div :class="getStepIndexClassName(1)">1</div>
          <div :class="getStepNameClassName(1)">区域编辑</div>
        </div>
        <div class="step-connector-line" />
        <div class="step-item-box">
          <div :class="getStepIndexClassName(2)">2</div>
          <div :class="getStepNameClassName(2)">属性编辑</div>
        </div>
      </div>
      <div class="water-name-box">
        <div class="name-box">
          <div class="label">名称：</div>
          <div
            class="name text-overflow"
            :title="waterName">
            {{ waterName }}
          </div>
        </div>
        <div
          class="common-button-box"
          v-show="currStep === 1">
          <el-button
            size="small"
            class="normal-btn"
            @click="exportWaterReg">
            导出绘制区域
          </el-button>
        </div>
      </div>
      <div
        class="first-step-box"
        v-show="currStep === 1">
        <div class="uniform-height-box">
          <div class="label-box">
            <div class="name">统一顶点高度</div>
            <el-tooltip
              :effect="$theme.tooltipKey()"
              placement="top-start"
              popper-class="common-panel-tooltip-box">
              <div slot="content">
                在绘制矢量面时，由于手动操作可能存在偏差，各顶点可能出现高低不平的情况。为确保图形平整，您可在完成绘制后使用高度统一功能，一键将所有顶点智能对齐到相同水平面，消除高度误差
              </div>
              <div class="icon-box">
                <icon-svg icon-class="ic-jieshishuoming" />
              </div>
            </el-tooltip>
            <div>:</div>
          </div>
          <div
            :class="`icon-btn-box ${isPickUpHeightEnable ? 'active' : ''}`"
            @click="pickUpHeight">
            <icon-svg icon-class="icon-shiqubingtongyidingdiangaodu" />
          </div>
        </div>
      </div>
      <div
        class="last-step-box"
        v-show="currStep === 2">
        <div class="basic-param-box">
          <div class="color-box">
            <div class="label-box">颜色：</div>
            <ColorPicker
              ref="colorPicker"
              :defaultColor="waterColor"
              :recentColorList="recentColorList"
              :recommendColorList="recommendColorList"
              :onColorChange="onColorPickerChange" />
          </div>
          <div class="opacity-box">
            <div class="label-box">水面透明度：</div>
            <div class="opacity-slider-box">
              <div class="slider-box">
                <el-slider
                  input-size="small"
                  v-model="waterOpacity"
                  :max="1"
                  :step="0.1"
                  :show-tooltip="false"
                  @change="onColorPickerChange" />
              </div>
              <div class="slider-value">{{ waterOpacity }}</div>
            </div>
          </div>
        </div>
        <div class="senior-param-box">
          <div
            class="title-box"
            @click="toggleSeniorParam">
            <div>高级参数</div>
            <div class="icon-box">
              <i
                class="el-icon-arrow-up"
                v-if="isSeniorParamsVisible" />
              <i
                class="el-icon-arrow-down"
                v-else />
            </div>
          </div>
          <div
            class="param-content-box"
            v-if="isSeniorParamsVisible">
            <div class="input-item-box">
              <div class="item-box">
                <div class="label-box">可视距离：</div>
                <div class="common-input-box">
                  <el-input-number
                    size="small"
                    :controls="false"
                    v-model="visDist"
                    @change="value => onSeniorParamChange('visDist', value)" />
                </div>
                <div class="unit-box">km</div>
              </div>
              <div class="item-box">
                <div class="label-box">外扩：</div>
                <div class="common-input-box">
                  <el-input-number
                    size="small"
                    :controls="false"
                    v-model="expandDist"
                    @change="
                      value => onSeniorParamChange('expandDist', value)
                    " />
                </div>
                <div class="unit-box">m</div>
              </div>
            </div>
            <div class="slider-item-box">
              <div class="item-box">
                <div class="label-box">混合系数：</div>
                <el-slider
                  class="slider-primary"
                  input-size="small"
                  v-model="blendDist"
                  :min="0"
                  :max="1"
                  :step="0.1"
                  @change="value => onSeniorParamChange('blendDist', value)" />
              </div>
            </div>
            <div class="slider-item-box">
              <div class="item-box">
                <div class="label-box">深度偏移：</div>
                <el-slider
                  class="slider-primary"
                  input-size="small"
                  v-model="depthBias"
                  :min="-0.0001"
                  :max="0.0001"
                  :step="0.00001"
                  @change="value => onSeniorParamChange('depthBias', value)" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="operation-box">
      <div
        class="common-button-box"
        v-if="currStep === 1">
        <el-button
          size="small"
          class="primary-btn primary-ang-btn"
          type="primary"
          @click="nextStep">
          下一步
        </el-button>
      </div>
      <div
        class="common-button-box"
        v-if="currStep === 2">
        <el-button
          size="small"
          class="normal-btn primary-ang-btn"
          @click="lastStep">
          上一步
        </el-button>
        <el-button
          class="primary-btn primary-ang-btn"
          size="small"
          type="primary"
          @click="saveWater">
          保存
        </el-button>
      </div>
    </div>
  </DragPanel>
</template>

<script>
import DragPanel from '@/components/CustomDragPanel.vue'
import ColorPicker from '@/components/ColorPicker.vue'

import { getWaterColorsService, editWaterService } from '@/service/interface.js'

export default {
  data() {
    return {
      currStep: 1,
      activeSteps: [1],

      waterName: '水面名称',
      isPickUpHeightEnable: false,

      waterColor: '',
      waterOpacity: 0,

      recentColorList: [],
      recommendColorList: [],
      isSeniorParamsVisible: true,
      blendDist: 0,
      visDist: 200,
      depthBias: 0,
      expandDist: 0,

      // 当前选中的矢量边，作为是否给出操作提示的依据
      currElemType: '',
      currEdgeId: '',
      originX: -1,
      originY: -1
    }
  },
  props: {
    waterNodeInfo: Object,
    updateSceneTree: Function,
    closeWaterPanel: Function
  },
  components: {
    DragPanel,
    ColorPicker
  },
  mounted() {
    this.init()

    document.addEventListener('RESystemSelElement', this.onSelElement)
    document.addEventListener('RESystemSelShpElement', this.onSelElement)
    this.addCanvasEventListener()
  },
  beforeDestroy() {
    document.removeEventListener('RESystemSelElement', this.onSelElement)
    document.removeEventListener('RESystemSelShpElement', this.onSelElement)
    this.removeCanvasEventListener()
  },
  methods: {
    init() {
      this.initWaterColors()
      let waterNodeInfo = this.waterNodeInfo
      this.waterName = waterNodeInfo.nodeName

      let waterInfo = waterNodeInfo.waterInfo
      this.waterColor = waterInfo.color
      this.waterOpacity = Number((1 - waterInfo.transparency).toFixed(1))
      this.blendDist = waterInfo.blendDist
      this.visDist = waterInfo.visDist / 1000
      this.depthBias = waterInfo.depthBias
      this.expandDist = waterInfo.expandDist

      const Water = window.BlackHole3D.Water
      Water.setCamToData([waterInfo.id])
      Water.startEditState()
      Water.setShowState([waterInfo.id], 0)

      this.setPanelStyle()
    },
    initWaterColors() {
      getWaterColorsService().then(response => {
        if (response.isSuccess) {
          let data = response.data
          this.recentColorList = data.commonColors
          this.recommendColorList = data.intrinsicColors
        } else {
          this.$message.error(response.errMsg)
        }
      })
    },
    exportWaterReg() {
      let waterInfo = this.waterNodeInfo.waterInfo
      let waterID = waterInfo.id
      let regGeoJson = this.getWaterRegData(waterID)
      regGeoJson = JSON.stringify(regGeoJson)
      // 创建一个 Blob 对象，指定类型为 JSON
      const blob = new Blob([regGeoJson], { type: 'application/json' })

      // 创建一个链接元素
      const link = document.createElement('a')
      link.href = URL.createObjectURL(blob) // 生成下载链接
      link.download = `${this.waterName}.json` // 设置下载文件名

      // 触发下载
      link.click()

      // 释放 URL 对象
      URL.revokeObjectURL(link.href)
    },
    pickUpHeight() {
      this.isPickUpHeightEnable = true
    },
    nextStep() {
      const Water = window.BlackHole3D.Water
      Water.endAddWaterState()
      Water.endEditState()

      this.currStep = 2
      if (!this.activeSteps.includes(2)) {
        this.activeSteps.push(2)
      }

      this.setPanelStyle()
    },
    lastStep() {
      let waterInfo = this.waterNodeInfo.waterInfo
      let waterID = waterInfo.id

      const Water = window.BlackHole3D.Water
      Water.startEditState()
      Water.setShowState([waterID], 0)

      this.currStep = 1
      this.setPanelStyle()
    },
    saveWater() {
      this.saveWaterRequest().then(() => {
        this.updateSceneTree().then(() => {
          this.$message.success('保存成功')
          this.closeWaterPanel()
        })
      })
    },
    saveWaterRequest() {
      return new Promise(resolve => {
        const Water = window.BlackHole3D.Water
        let waterInfo = this.waterNodeInfo.waterInfo
        let waterID = waterInfo.id
        let domRef = this.$refs.colorPicker
        let color = domRef.getHexColor()
        let waterData = Water.getData([waterID])
        let waterDataInfo = waterData[0]
        let transparency = Number((1 - this.waterOpacity).toFixed(1))

        let params = {
          nodeId: this.waterNodeInfo.nodeId,
          color,
          transparency,
          geojson: JSON.stringify(waterDataInfo),
          blendDist: this.blendDist,
          expandDist: this.expandDist,
          depthBias: this.depthBias,
          visDist: this.visDist * 1000
        }
        editWaterService(params).then(response => {
          if (response.isSuccess) {
            resolve(response)
          } else {
            this.$message.error(response.errMsg)
          }
        })
      })
    },
    onColorPickerChange() {
      let waterNodeInfo = this.waterNodeInfo
      let waterID = waterNodeInfo.waterInfo.id
      this.setWaterColor(waterID)
    },
    closePanel() {
      return new Promise(resolve => {
        let isCheckPass = this.isUnSaveCheck()

        if (isCheckPass) {
          if (this.currStep === 1) {
            window.BlackHole3D.Water.endEditState()
          }
          this.closeWaterPanel()
          resolve()
          return
        }

        this.$confirm('您编辑的内容未被保存，确定要退出吗？', '提示', {
          confirmButtonText: '保存并退出',
          cancelButtonText: '直接退出',
          distinguishCancelAndClose: true,
          type: 'warning'
        })
          .then(() => {
            const BlackHole3D = window.BlackHole3D
            if (this.currStep === 1) {
              BlackHole3D.Water.endEditState()
            }
            this.saveWater()
            resolve()
          })
          .catch(e => {
            if (e === 'cancel') {
              this.resetWater()
              this.closeWaterPanel()
              resolve()
            }
          })
      })
    },
    resetWater() {
      const BlackHole3D = window.BlackHole3D
      if (this.currStep === 1) {
        BlackHole3D.Water.endEditState()
      }

      let waterNodeInfo = this.waterNodeInfo
      let waterInfo = waterNodeInfo.waterInfo
      let waterGeoJson = JSON.parse(waterInfo.geoJson)
      let rgnList = waterGeoJson.rgnList
      let rgnInfo = rgnList[0]
      let cornerRgnInfo = new BlackHole3D.RECornerRgnInfo()
      cornerRgnInfo.pointList = rgnInfo.pointList
      cornerRgnInfo.indexList = rgnInfo.indexList

      let waterRgnList = [cornerRgnInfo]
      let { red, green, blue, alpha } = waterGeoJson.waterClr
      let waterClr = new BlackHole3D.REColor(red, green, blue, alpha)
      let waterInstance = new BlackHole3D.REWaterInfo()

      waterInstance.waterName = waterInfo.id
      waterInstance.waterClr = waterClr
      waterInstance.blendDist = waterGeoJson.blendDist
      waterInstance.visible = waterGeoJson.visible
      waterInstance.expandDist = waterGeoJson.expandDist
      waterInstance.depthBias = waterGeoJson.depthBias
      waterInstance.visDist = waterGeoJson.visDist
      waterInstance.rgnList = waterRgnList

      BlackHole3D.Water.setData([waterInstance])
    },
    isUnSaveCheck() {
      let isCheckPass = true

      let isWaterRegChange = this.isRegChange()
      let isWaterAttrChange = this.isAttrChange()
      let isSeniorParamChange = this.isSeniorParamChange()

      if (isWaterRegChange || isWaterAttrChange || isSeniorParamChange) {
        isCheckPass = false
      }

      return isCheckPass
    },
    isRegChange() {
      let waterInfo = this.waterNodeInfo.waterInfo
      let geoJson = JSON.parse(waterInfo.geoJson)
      let originRgnList = geoJson.rgnList
      let originRgnInfo = originRgnList[0]
      let originPointList = originRgnInfo.pointList

      const Water = window.BlackHole3D.Water
      let waterID = waterInfo.id
      let waterData = Water.getData([waterID])
      let newWaterInfo = waterData[0]
      let newRgnList = newWaterInfo.rgnList
      let newRgnInfo = newRgnList[0]
      let newPointList = newRgnInfo.pointList

      let isChange = this.isArrayChanged(originPointList, newPointList)
      return isChange
    },
    isAttrChange() {
      let waterInfo = this.waterNodeInfo.waterInfo
      let originColor = waterInfo.color.toUpperCase()
      let originTransparency = waterInfo.transparency

      let domRef = this.$refs.colorPicker
      let newColor = domRef.getHexColor().toUpperCase()
      let newTransparency = Number((1 - this.waterOpacity).toFixed(1))

      let isChange = false
      if (originColor !== newColor || originTransparency !== newTransparency) {
        isChange = true
      }

      return isChange
    },
    isSeniorParamChange() {
      let isChange = false
      let waterInfo = this.waterNodeInfo.waterInfo
      let originBlendDist = waterInfo.blendDist
      let originVisDist = waterInfo.visDist / 1000
      let originDepthBias = waterInfo.depthBias
      let originExpandDist = waterInfo.expandDist

      if (
        originBlendDist !== this.blendDist ||
        originVisDist !== this.visDist ||
        originDepthBias !== this.depthBias ||
        originExpandDist !== this.expandDist
      ) {
        isChange = true
      }
      return isChange
    },
    isArrayChanged(originPoints, newPoints) {
      if (originPoints.length !== newPoints.length) return true

      return originPoints.some((point1, index) => {
        const point2 = newPoints[index]
        // prettier-ignore
        return  point1[0] !== point2[0] || point1[1] !== point2[1] || point1[2] !== point2[2];
      })
    },
    setWaterColor(waterID) {
      const BlackHole3D = window.BlackHole3D

      let domRef = this.$refs.colorPicker
      // prettier-ignore
      let transparency = Math.round(Number((1 - this.waterOpacity).toFixed(1)) * 255);
      let color = domRef.getColor().concat(transparency)
      // prettier-ignore
      let waterColor = new BlackHole3D.REColor(color[0], color[1], color[2], color[3]);

      BlackHole3D.Water.setClr(waterID, waterColor)
    },
    // 处理元素点击事件(包括普通数据和矢量数据点击)，用于拾取高度
    onSelElement(eventInfo) {
      const BlackHole3D = window.BlackHole3D
      let currProbeRet = BlackHole3D.Probe.getCurCombProbeRet()
      let { elemType, elemId } = currProbeRet

      if (this.currEdgeId === elemId) {
        this.currElemType = ''
        this.currEdgeId = ''
        return
      }

      if (elemType === 'ShapeElem') {
        if (elemId && elemId.includes('WATER_EDGE')) {
          // this.$message.info("再次点击选中边可以添加中点");

          this.currElemType = 'WATER_EDGE'
          this.currEdgeId = elemId
        } else {
          this.currElemType = ''
        }
      } else {
        this.currElemType = ''
        this.currEdgeId = ''
      }

      if (!this.isPickUpHeightEnable) return

      // 右键取消拾取高度
      let eventDetail = eventInfo.detail
      if (eventDetail.button === 1) {
        this.isPickUpHeightEnable = false

        let coorInfo = { visible: false }
        this.$eventManage.fire('canvasMouseMove', { coorInfo })
        return
      }

      let currPos = currProbeRet.elemPos
      if (currPos.length) {
        let height = Number(currPos[2].toFixed(2))

        const Water = window.BlackHole3D.Water

        let waterNodeInfo = this.waterNodeInfo
        let waterID = waterNodeInfo.waterInfo.id
        let waterData = Water.getData([waterID])
        let waterInfo = waterData[0]
        let rgnList = waterInfo.rgnList
        let cornerRgnInfo = rgnList[0]
        let pointList = cornerRgnInfo.pointList
        pointList.forEach(point => {
          point[2] = height
        })

        Water.setData(waterData)

        Water.endEditState()
        Water.startEditState()
        Water.setShowState([waterID], 0)
      }

      this.isPickUpHeightEnable = false

      let coorInfo = { visible: false }
      this.$eventManage.fire('canvasMouseMove', { coorInfo })
    },
    addCanvasEventListener() {
      let domRef = null
      if (window.BlackHole3D) {
        domRef = window.BlackHole3D.canvas
      } else {
        domRef = document.getElementById('engineCanvas')
      }
      domRef.addEventListener('click', this.onCanvasClick)
      domRef.addEventListener('mouseup', this.onCanvasMouseUp)
      domRef.addEventListener('mousemove', this.onCanvasMouseMove)
      domRef.addEventListener('mouseleave', this.onCanvasMouseLeave)
    },
    removeCanvasEventListener() {
      let domRef = null
      if (window.BlackHole3D) {
        domRef = window.BlackHole3D.canvas
      } else {
        domRef = document.getElementById('engineCanvas')
      }
      domRef.removeEventListener('click', this.onCanvasClick)
      domRef.removeEventListener('mouseup', this.onCanvasMouseUp)
      domRef.removeEventListener('mousemove', this.onCanvasMouseMove)
      domRef.removeEventListener('mouseleave', this.onCanvasMouseLeave)
    },
    onCanvasClick(eventInfo) {
      // 由于canvaClick事件早于RESystemSelShpElement事件触发，因此增加延迟，以获取RESystemSelShpElement事件中拿到的数据
      setTimeout(() => {
        if (this.currElemType === 'WATER_EDGE' && this.currEdgeId) {
          this.originX = eventInfo.clientX
          this.originY = eventInfo.clientY

          let coorInfo = {
            visible: true,
            tipContent: '再次点击选中边可以添加中点',
            clientX: eventInfo.clientX,
            clientY: eventInfo.clientY
          }
          this.$eventManage.fire('canvasMouseMove', { coorInfo })
        } else {
          this.originX = -1
          this.originY = -1
          let coorInfo = { visible: false }
          this.$eventManage.fire('canvasMouseMove', { coorInfo })
        }
      }, 300)
    },
    onCanvasMouseUp(eventInfo) {
      let deletaX = eventInfo.clientX - this.originX
      let deletaY = eventInfo.clientY - this.originY
      if (Math.abs(deletaX) > 5 && Math.abs(deletaY) > 5) {
        this.currElemType = ''
      }
    },
    onCanvasMouseMove(event) {
      if (this.currElemType === 'WATER_EDGE' && this.currEdgeId) {
        if (this.originX !== -1 && this.originY !== -1) {
          let deletaX = event.clientX - this.originX
          let deletaY = event.clientY - this.originY
          if (Math.abs(deletaX) > 5 && Math.abs(deletaY) > 5) {
            this.originX = -1
            this.originY = -1
            let coorInfo = { visible: false }
            this.$eventManage.fire('canvasMouseMove', { coorInfo })
          }
        }
        return
      }

      if (!this.isPickUpHeightEnable) return

      let tipContent = '左键拾取高度并应用，右键取消'

      let coorInfo = {
        visible: true,
        tipContent,
        clientX: event.clientX,
        clientY: event.clientY
      }
      this.$eventManage.fire('canvasMouseMove', { coorInfo })
    },
    onCanvasMouseLeave() {
      let coorInfo = { visible: false }
      this.$eventManage.fire('canvasMouseMove', { coorInfo })
    },
    toggleSeniorParam() {
      this.isSeniorParamsVisible = !this.isSeniorParamsVisible
      this.setPanelStyle()
    },
    onSeniorParamChange(paramName, paramValue) {
      const Water = window.BlackHole3D.Water

      let waterNodeInfo = this.waterNodeInfo
      let waterID = waterNodeInfo.waterInfo.id
      if (paramName === 'blendDist') {
        Water.setBlendDist(waterID, paramValue)
        return
      }
      if (paramName === 'visDist') {
        let visDist = paramValue * 1000
        Water.setVisDist(waterID, visDist)
        return
      }
      if (paramName === 'depthBias') {
        Water.setDepthBias(waterID, paramValue)
        return
      }
      if (paramName === 'expandDist') {
        Water.setExpandDist(waterID, paramValue)
        return
      }
    },
    setPanelStyle() {
      this.$nextTick(() => {
        let domRef = this.$refs.dom.$el
        let panelHeaderDom = domRef.querySelector('.header-box')
        let panelContentDom = domRef.querySelector('.content')
        let height = panelHeaderDom.offsetHeight + panelContentDom.offsetHeight

        domRef.style.height = `${height}px`
        domRef.style.minHeight = `${height}px`
      })
    },
    getWaterRegData(waterID) {
      const Water = window.BlackHole3D.Water

      let waterData = Water.getData([waterID])
      let waterInfo = waterData[0]
      let rgnList = waterInfo.rgnList
      let rgnInfo = rgnList[0]
      let pointList = rgnInfo.pointList
      let transformedCoordList = this.getTransformedCoordList(pointList)

      let coordinates = transformedCoordList.map(item => {
        return item.slice(0, 2)
      })
      let heights = transformedCoordList.map(item => {
        return item.slice(2)[0]
      })

      let geoJson = {
        type: 'FeatureCollection',
        features: [
          {
            type: 'Feature',
            geometry: {
              type: 'Polygon',
              coordinates: [coordinates]
            },
            properties: {
              heights
            }
          }
        ]
      }

      return geoJson
    },
    getTransformedCoordList(sourceCoordList) {
      const Coordinate = window.BlackHole3D.Coordinate
      const targetDataCrs = 'EPSG:4326'

      let transformedCoordList = []
      let engineWorldCrs = Coordinate.getEngineWorldCRS()
      if (engineWorldCrs) {
        sourceCoordList.forEach(item => {
          // prettier-ignore
          let point = Coordinate.getTransEngineCoords(true, targetDataCrs, [item]);
          transformedCoordList.push(point[0])
        })
      } else {
        let destCRS = 'EPSG:3857'
        sourceCoordList.forEach(item => {
          item.push(0)
        })
        // prettier-ignore
        transformedCoordList = Coordinate.getTransGeoCoords(destCRS, targetDataCrs, sourceCoordList);
      }

      // transformedCoordList = transformedCoordList.map((item) => {
      //   return item.slice(0, 2);
      // });

      // 判断数据是否首尾点重合，防止导出数据导入时，自相交校验失败
      let firstPoint = transformedCoordList[0]
      let lastPoint = transformedCoordList[transformedCoordList.length - 1]
      if (firstPoint[0] !== lastPoint[0] || firstPoint[1] !== lastPoint[1]) {
        transformedCoordList.push(firstPoint)
      }

      return transformedCoordList
    },
    getStepIndexClassName(step) {
      let className = 'step-index-box'
      if (this.activeSteps.includes(step)) {
        className += ' actived'
      }
      if (this.currStep === step) {
        className += ' active'
      }
      return className
    },
    getStepNameClassName(step) {
      let className = 'step-name-box'
      if (this.activeSteps.includes(step)) {
        className += ' active'
      }
      return className
    },
    // 打开其他面板前给出提示
    openOtherPanelCheck() {
      return new Promise(resolve => {
        let isWaterRegChange = this.isRegChange()
        let isWaterAttrChange = this.isAttrChange()
        let isSeniorParamChange = this.isSeniorParamChange()

        if (!isWaterRegChange && !isWaterAttrChange && !isSeniorParamChange) {
          if (this.currStep === 1) {
            window.BlackHole3D.Water.endEditState()
          }
          this.closeWaterPanel()
          resolve(true)
        } else {
          // prettier-ignore
          let message = '当前正在编辑水面，请先结束编辑再执行相关操作';
          let options = {
            message,
            type: 'warning',
            duration: 5000,
            showClose: true
          }
          this.$message(options)
          resolve(false)
        }
      })
    }
  }
}
</script>

<style lang="less" scoped>
@import '@/assets/css/publishSearch.less';
.common-drag-panel {
  left: 6px;
  top: 6px;
  width: 360px;
  height: unset;
  min-width: 360px;

  .common-button-box {
    display: flex;
    justify-content: end;

    .el-button {
      margin-right: 0px;
      border-radius: 4px;
    }
  }

  .edit-water-box {
    padding: 15px;

    .steps-box {
      display: flex;
      padding: 10px 15px;
      border-radius: 5px;

      .step-item-box {
        display: flex;
        align-items: center;
        flex-shrink: 0;
        font-size: 14px;

        .step-index-box {
          width: 16px;
          height: 16px;
          font-size: 12px;
          border: 1px solid var(--extrude-step-line);
          border-radius: 50%;
          line-height: 14px;

          &.active.actived {
            color: var(--aside-bg-color);
            border-color: var(--base-primary-color);
            background-color: var(--base-primary-color);
          }

          &.active {
            color: var(--extrude-step-text-color);
            border-color: var(--extrude-step-line);
          }
        }

        .step-name-box {
          margin-left: 10px;
          color: var(--extrude-step-text-color);

          &.active.actived {
            color: var(--extrude-step-text-color);
          }

          &.active {
            color: var(--base-primary-color);
          }
        }
      }

      .step-connector-line {
        flex-grow: 1;
        margin: auto 10px;
        height: 2px;
        background: var(--extrude-step-line);
      }
    }

    .water-name-box {
      display: flex;
      align-items: center;
      margin-top: 15px;
      font-size: 14px;
      line-height: 32px;

      .name-box {
        flex-grow: 1;
        display: flex;
        text-align: left;
        overflow: hidden;

        .label {
          flex-shrink: 0;
        }
      }
    }

    .first-step-box {
      margin-top: 16px;

      .uniform-height-box {
        display: flex;
        align-items: center;

        .label-box {
          display: flex;
          align-items: center;
          flex-shrink: 0;

          .name {
            font-size: 14px;
          }

          .icon-box {
            margin: 0px 3px;

            span {
              margin-top: 2px;
            }
          }
        }

        .icon-btn-box {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 32px;
          height: 32px;
          margin-left: 10px;
          border-radius: 4px;
          color: var(--extrude-texture-drawbtn-icon-color);
          cursor: pointer;
          background: var(--extrude-texture-drawbtn-bgColor);
          border: 1px solid var(--extrude-texture-drawbtn-boder-color);

          &.active {
            background: var(--extrude-texture-drawbtn-hover-bgColor);
          }

          &:hover {
            background: var(--extrude-texture-drawbtn-hover-bgColor);
          }
        }
      }
    }

    .last-step-box {
      margin-top: 10px;
      font-size: 14px;

      .basic-param-box {
        display: flex;
        align-items: center;

        .color-box {
          display: flex;
          align-items: center;

          :deep(.color-picker-container) {
            text-align: left;

            .picker-box {
              margin-top: 2px;
              border-radius: 3px;

              .pickr {
                .pcr-button {
                  width: 16px;
                  height: 16px;
                  &::before {
                    background: transparent;
                    border-radius: 3px 0px 0px 3px;
                  }
                  &::after {
                    border-radius: 3px 0px 0px 3px;
                  }
                }
              }

              .open-btn-box {
                display: flex;
                background: var(--color-picker-open-btn-bgColor);
                border-radius: 0px 3px 3px 0px;

                i {
                  margin: auto;
                }
              }
            }
          }
        }

        .opacity-box {
          flex-grow: 1;
          display: flex;
          align-items: center;
          margin-left: 10px;

          .opacity-slider-box {
            flex-grow: 1;
            display: flex;
            align-items: center;

            :deep(.slider-box) {
              flex-grow: 1;
              margin-left: 5px;

              .el-slider {
                .el-slider__runway {
                  background: var(--base-bg-light-color81),
                    url('@/assets/img/opacitySlider.svg');

                  .el-slider__bar {
                    background: transparent;
                  }

                  .el-slider__button-wrapper {
                    .el-slider__button {
                      border: 2px solid var(--base-primary-bgColor);
                      background-color: #ffffff;
                    }
                  }
                }
              }
            }

            .slider-value {
              width: 25px;
              flex-shrink: 0;
              margin-left: 10px;
            }
          }
        }
      }

      .senior-param-box {
        margin: 10px 0px;
        text-align: left;
        border-radius: 8px;
        background: var(--water-senior-params-bgColor);
        border: 1px solid var(--water-senior-params-border-bgColor);

        .title-box {
          display: flex;
          align-items: center;
          padding: 10px;
          border-radius: 8px 8px 0px 0px;
          background: var(--water-senior-params-title-bgColor);
          cursor: pointer;

          .icon-box {
            margin-left: 10px;
          }
        }

        .param-content-box {
          padding: 8px 10px;

          .input-item-box {
            display: flex;
            align-items: center;
          }

          .slider-item-box {
            .slider-primary {
              flex-grow: 1;
              padding: 0px 10px;
            }
          }

          .item-box {
            display: flex;
            align-items: center;

            .label-box {
              flex-shrink: 0;
              width: 70px;
              text-align: right;
            }

            .common-input-box {
              flex-grow: 1;
              :deep(.el-input-number--small) {
                width: 100%;

                .el-input__inner {
                  padding: 0px 5px;
                  text-align: left;
                }
              }
            }

            .unit-box {
              margin-left: 3px;
            }
          }
        }
      }
    }
  }

  .operation-box {
    border-top: 1px solid var(--extrude-step-footer-border-color);
    padding: 15px;
  }
}
</style>
